import { useEffect, useRef, useState } from "react"

/**
 * Accessible replacement for window.confirm()/window.prompt(), styled consistently
 * with the rest of the admin console instead of a native, unstyled browser dialog.
 *
 * Usage: const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm()
 *        <ConfirmDialog state={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />
 *        const ok = await confirm("Delete this request?")
 */
export default function ConfirmDialog({ state, onConfirm, onCancel }) {
    const dialogRef = useRef(null)
    const previouslyFocused = useRef(null)
    const [inputValue, setInputValue] = useState("")

    const isOpen = !!state
    const isPrompt = !!state?.inputLabel

    useEffect(() => {
        if (!isOpen) return
        setInputValue(state?.inputDefault || "")
        previouslyFocused.current = document.activeElement

        const focusTimer = setTimeout(() => {
            const target = dialogRef.current?.querySelector("input, textarea, [data-autofocus]")
            target?.focus()
        }, 0)

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault()
                onCancel()
                return
            }
            if (e.key === "Tab") {
                const focusables = dialogRef.current?.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
                if (!focusables || focusables.length === 0) return
                const first = focusables[0]
                const last = focusables[focusables.length - 1]
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault()
                    last.focus()
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault()
                    first.focus()
                }
            }
        }

        document.addEventListener("keydown", handleKeyDown, true)
        return () => {
            clearTimeout(focusTimer)
            document.removeEventListener("keydown", handleKeyDown, true)
            previouslyFocused.current?.focus?.()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    if (!isOpen) return null

    const danger = !!state.danger
    const confirmLabel = state.confirmLabel || (isPrompt ? "Submit" : "Confirm")
    const cancelLabel = state.cancelLabel || "Cancel"

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 100000,
                background: "rgba(15, 23, 42, 0.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "20px",
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}
        >
            <div
                ref={dialogRef}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="confirm-dialog-title"
                aria-describedby={state.message ? "confirm-dialog-message" : undefined}
                style={{
                    background: "#fff", borderRadius: "14px", width: "min(440px, 100%)",
                    boxShadow: "0 24px 64px rgba(0,43,84,0.18), 0 8px 24px rgba(0,43,84,0.10)",
                    padding: "24px",
                }}
            >
                <h2 id="confirm-dialog-title" style={{ fontSize: "16px", fontWeight: 800, color: "#002B54", marginBottom: "8px" }}>
                    {state.title || (danger ? "Are you sure?" : "Please confirm")}
                </h2>
                {state.message && (
                    <p id="confirm-dialog-message" style={{ fontSize: "13px", color: "#6B778C", lineHeight: 1.5, marginBottom: isPrompt ? "12px" : "20px" }}>
                        {state.message}
                    </p>
                )}
                {isPrompt && (
                    <div style={{ marginBottom: "20px" }}>
                        {state.inputLabel && (
                            <label htmlFor="confirm-dialog-input" style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#344563", marginBottom: "6px" }}>
                                {state.inputLabel}
                            </label>
                        )}
                        <textarea
                            id="confirm-dialog-input"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            rows={3}
                            style={{
                                width: "100%", height: "72px", minHeight: "72px", maxHeight: "72px",
                                padding: "10px 12px", borderRadius: "8px",
                                border: "1px solid #DFE1E6", fontSize: "13px", fontFamily: "inherit",
                                resize: "none", overflowY: "auto", outline: "none", boxSizing: "border-box",
                            }}
                        />
                    </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            padding: "9px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 700,
                            border: "1.5px solid #DFE1E6", background: "#fff", color: "#344563", cursor: "pointer",
                        }}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        data-autofocus={!isPrompt ? "true" : undefined}
                        onClick={() => onConfirm(isPrompt ? inputValue : true)}
                        style={{
                            padding: "9px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 700,
                            border: "none", cursor: "pointer", color: "#fff",
                            background: danger
                                ? "linear-gradient(135deg, #dc2626, #b91c1c)"
                                : "linear-gradient(135deg, #002B54, #1e4070)",
                        }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    )
}
